import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  PenToolIcon,
  EraserIcon,
  Trash2Icon,
  RotateCcwIcon,
  CheckIcon,
  XIcon,
} from "lucide-react";
import type { SignatureStroke } from "./SignatureTypes";

type SignaturePadEditableProps = {
  mode: "inline" | "fullscreen";
  initialStrokes: SignatureStroke[];
  onChange?: (strokes: SignatureStroke[], pngDataUrl: string) => void;
  onDone?: (strokes: SignatureStroke[], pngDataUrl: string) => void;
  onCancel?: () => void;
  showConfirmRow: boolean;
};

export function SignaturePadEditable({
  mode,
  initialStrokes,
  onChange,
  onDone,
  onCancel,
  showConfirmRow,
}: SignaturePadEditableProps) {
  const sigRef = useRef<SignatureCanvas | null>(null);

  const [localStrokes, setLocalStrokes] = useState<SignatureStroke[]>(initialStrokes || []);
  const [isEraser, setIsEraser] = useState(false);

  const penColor = isEraser ? "#ffffff" : "#000000";

  useEffect(() => {
    setLocalStrokes(initialStrokes || []);
  }, [initialStrokes]);

  const redrawFromLocal = useCallback(() => {
    if (!sigRef.current) return;
    sigRef.current.clear();
    try {
      sigRef.current.fromData(localStrokes as any);
    } catch {
      /* ignore malformed */
    }
  }, [localStrokes]);

  const syncUpstreamIfInline = useCallback(() => {
    if (mode === "inline" && onChange && sigRef.current) {
      const pngNow = sigRef.current.toDataURL("image/png");
      onChange(localStrokes, pngNow);
    }
  }, [mode, onChange, localStrokes]);


  useEffect(() => {
    redrawFromLocal();
    syncUpstreamIfInline();
  }, [localStrokes, redrawFromLocal, syncUpstreamIfInline]);

  useEffect(() => {
    function handleResize() {
      redrawFromLocal();
    }

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [redrawFromLocal]);

  const pullFromCanvas = useCallback(() => {
    if (!sigRef.current) return;
    const data = sigRef.current.toData() as unknown as SignatureStroke[];
    setLocalStrokes(data);
  }, []);

  const handleUndo = useCallback(() => {
    setLocalStrokes((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -1);
    });
  }, []);

  const handleClear = useCallback(() => {
    setLocalStrokes([]);
  }, []);

  const handleEndStroke = useCallback(() => {
    pullFromCanvas();
  }, [pullFromCanvas]);

  const handleDone = useCallback(() => {
    if (!onDone || !sigRef.current) return;
    const png = sigRef.current.toDataURL("image/png");
    onDone(localStrokes, png);
  }, [onDone, localStrokes]);

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className={`
          border-b border-neutral-200 p-2 text-neutral-700
          flex flex-wrap items-center gap-2
          sm:flex-nowrap sm:justify-between
        `}
      >
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setIsEraser(false)}
            className={`flex flex-shrink-0 items-center gap-1 rounded px-2 py-1 text-sm font-medium ${!isEraser
              ? "bg-neutral-800 text-white"
              : "bg-white text-neutral-700 border border-neutral-300"
              }`}
          >
            <PenToolIcon className="h-4 w-4" />
            <span>Pen</span>
          </button>

          <button
            type="button"
            onClick={() => setIsEraser(true)}
            className={`flex flex-shrink-0 items-center gap-1 rounded px-2 py-1 text-sm font-medium ${isEraser
              ? "bg-neutral-800 text-white"
              : "bg-white text-neutral-700 border border-neutral-300"
              }`}
          >
            <EraserIcon className="h-4 w-4" />
            <span>Gom</span>
          </button>

          <button
            type="button"
            onClick={handleUndo}
            className="flex flex-shrink-0 items-center gap-1 rounded border border-neutral-300 bg-white px-2 py-1 text-sm font-medium"
          >
            <RotateCcwIcon className="h-4 w-4" />
            <span>Undo</span>
          </button>

          <button
            type="button"
            onClick={handleClear}
            className="flex flex-shrink-0 items-center gap-1 rounded border border-neutral-300 bg-white px-2 py-1 text-sm font-medium text-red-600"
          >
            <Trash2Icon className="h-4 w-4" />
            <span>Leegmaken</span>
          </button>
        </div>

        {showConfirmRow && (
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <button
              type="button"
              onClick={handleCancel}
              className="flex flex-shrink-0 items-center gap-1 rounded border border-neutral-300 bg-white px-2 py-1 text-sm font-medium text-neutral-700"
            >
              <XIcon className="h-4 w-4" />
              <span>Annuleren</span>
            </button>

            <button
              type="button"
              onClick={handleDone}
              className="flex flex-shrink-0 items-center gap-1 rounded bg-green-600 px-2 py-1 text-sm font-medium text-white"
            >
              <CheckIcon className="h-4 w-4" />
              <span>Gereed</span>
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-1 items-center justify-center p-2">
        <div className="w-full rounded border border-neutral-300 bg-white shadow-inner">
          <SignatureCanvas
            ref={sigRef}
            penColor={penColor}
            minWidth={isEraser ? 8 : 1.5}
            maxWidth={isEraser ? 8 : 2.5}
            throttle={16}
            canvasProps={{
              className:
                "w-full h-[300px] md:h-[360px] block touch-none",
            }}
            onEnd={handleEndStroke}
          />
        </div>
      </div>

    </div>
  );
}
